import { useEffect, useRef, useState } from "react";

import { useAuth } from "@/hooks/use-auth";
import { useAppState } from "@/store/useApp";
import { config } from "@/config";

function buildMediaFilesUrl(filename: string): string {
  const normalized = filename.startsWith("/") ? filename.slice(1) : filename;
  return `${config.apiUrl}/media/files?filename=${encodeURIComponent(normalized)}`;
}

function isAbsolutePictureUrl(picture: string): boolean {
  return picture.startsWith("http");
}

export const UserAvatar = () => {
  const user = useAppState((state) => state.user);
  const { getAccessToken, logout, refreshAccessToken } = useAuth();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blobPicture, setBlobPicture] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  const picture = user?.picture;

  useEffect(() => {
    const revokeStored = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setBlobPicture(null);
    };

    if (!picture || isAbsolutePictureUrl(picture)) {
      revokeStored();
      setBlobUrl(null);
      setLoadError(false);
      return;
    }

    let cancelled = false;
    revokeStored();
    setBlobUrl(null);
    setLoadError(false);

    void (async () => {
      try {
        const fetchAvatar = async (token: string) => {
          return await fetch(buildMediaFilesUrl(picture), {
            headers: { Authorization: `Bearer ${token}` },
          });
        };

        let response = await fetchAvatar(await getAccessToken());

        if (response.status === 401 || response.status === 403) {
          try {
            response = await fetchAvatar(await refreshAccessToken());
          } catch {
            logout();
            return;
          }
        }
        if (response.status === 401 || response.status === 403) {
          logout();
          return;
        }
        if (!response.ok) {
          throw new Error("Failed to load avatar");
        }
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        objectUrlRef.current = objectUrl;
        setBlobPicture(picture);
        setBlobUrl(objectUrl);
      } catch {
        if (!cancelled) {
          setLoadError(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      revokeStored();
    };
  }, [picture, getAccessToken, logout, refreshAccessToken]);

  if (!user) {
    return null;
  }

  const directSrc =
    picture && isAbsolutePictureUrl(picture) ? picture : null;
  const proxiedSrc =
    picture &&
    !isAbsolutePictureUrl(picture) &&
    blobPicture === picture &&
    blobUrl &&
    !loadError
      ? blobUrl
      : null;
  const imageSrc = directSrc ?? proxiedSrc;

  const fallback = (
    <div
      className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-xs font-medium"
      aria-hidden
    >
      {user.nickname?.charAt(0).toUpperCase() ?? "?"}
    </div>
  );

  return (
    <div className="flex items-center gap-3 pl-4 pb-1">
      <div className="shrink-0">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={user.nickname}
            className="w-10 h-10 rounded-lg object-cover"
          />
        ) : (
          fallback
        )}
      </div>

      <div className="max-w-[140px]">
        <div className="text-xs truncate">{user.email}</div>
      </div>
    </div>
  );
};
